import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import Dashboard from "./components/Dashboard";
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import { AuthProvider, useAuth } from "./components/AuthProvider";
import { AlertProvider } from "./alertService";
import UpdateDialog from "./components/UpdateDialog";
import { esInversionista } from "./utils/roles";

// Dashboard Components
import Home from "./components/Home";
import AdminHome from "./components/AdminHome";
import VenuesList from "./components/VenuesList";
import VenuePage from "./components/VenuePage";
import CreateVenue from "./components/CreateVenue";
import Calendario from "./components/Calendario";
import EventDetails from "./components/EventDetails";
import Configuracion from "./components/Configuracion";
import Support from "./components/support/Support";
import Changelog from "./components/Changelog";
import Users from "./components/Users";
import VenueRequests from "./components/VenueRequests";
import OpenDataManager from "./components/OpenDataManager";
import CatalogosManager from "./components/CatalogosManager";
import EventTypesManager from "./components/EventTypesManager";
import CiudadesManager from "./components/CiudadesManager";
import TemasDestinoManager from "./components/TemasDestinoManager";
import ProveedoresCategoriasManager from "./components/ProveedoresCategoriasManager";
import ServiciosPage from "./components/servicios/ServiciosPage";
import ServicioDetallePage from "./components/servicios/ServicioDetallePage";
import ReportesPage from "./components/reportes/ReportesPage";
import ReportesAdminPage from "./components/reportes/ReportesAdminPage";
import ReporteDetalle from "./components/reportes/ReporteDetalle";
import DirectorioPage from "./components/directorio/DirectorioPage";
import ClientePage from "./components/directorio/ClientePage";
import ProveedorPage from "./components/ProveedorPage";
import EventPage from "./components/EventPage";
import Formularios from "./components/Formularios";
import LeadPage from "./components/LeadPage";
import FeedbackAdmin from "./components/FeedbackAdmin";
import CuentasShell from "./components/cuentas/CuentasShell";
import CuentasPorCobrar from "./components/cuentas/CuentasPorCobrar";
import CuentasBalance from "./components/cuentas/CuentasBalance";
import CuentasDepositos from "./components/cuentas/CuentasDepositos";
import CuentasPorPagar from "./components/cuentas/CuentasPorPagar";
import CuentasMovimientos from "./components/cuentas/CuentasMovimientos";
import UserProfile from "./components/UserProfile";
import Bitacora from "./components/Bitacora";
import BlogList from "./components/blog/BlogList";
import BlogForm from "./components/blog/BlogForm";
import AdminVenuePagos from "./components/AdminVenuePagos";
import AdminTelegramBoard from "./components/AdminTelegramBoard";

// Marketing — lazy-loaded so owners/viewers never download this chunk.
const MarketingTemplateList = React.lazy(() =>
  import("./components/marketing/TemplateList")
);
const MarketingTemplateEditor = React.lazy(() =>
  import("./components/marketing/TemplateEditor")
);
const MarketingCampaignList = React.lazy(() =>
  import("./components/marketing/CampaignList")
);
const MarketingCampaignEditor = React.lazy(() =>
  import("./components/marketing/CampaignEditor")
);
const MarketingFallback = () => (
  <div className="p-6 text-sm text-zinc-500">Cargando…</div>
);

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { userInfo } = useAuth();

  if (userInfo?.role !== "admin") {
    return <Navigate to="/dashboard/venues" replace />;
  }

  return children;
};

// Block viewers from specific routes. El inversionista sí pasa: ve todo lo
// que ve el owner, en solo lectura (P-FRA-13).
const NoViewerRoute = ({ children }) => {
  const { userInfo } = useAuth();

  if (userInfo?.role === "viewer") {
    return <Navigate to="/dashboard/home" replace />;
  }

  return children;
};

// Lo que el inversionista no ve (P-FRA-13): bitácora y Configuración
// completa, incluida la ficha del venue (que es la pestaña Venue de
// Configuración) y el alta de venue.
const NoInversionistaRoute = ({ children }) => {
  const { userInfo } = useAuth();

  if (esInversionista(userInfo)) {
    return <Navigate to="/dashboard/home" replace />;
  }

  return children;
};

// /dashboard/recursos/:id → /dashboard/servicios/:id (FRA-347).
const RedirectServicio = () => {
  const { servicioId } = useParams();
  return <Navigate to={`/dashboard/servicios/${servicioId}`} replace />;
};

function App() {
  return (
    <AlertProvider>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />

            {/* Private Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            >
              {/* Regular Home Route (accessible by all) */}
              <Route path="home" element={<Home />} />

              {/* Admin Only Routes */}
              <Route
                path="admin"
                element={
                  <AdminRoute>
                    <AdminHome />
                  </AdminRoute>
                }
              />
              <Route
                path="venue-pagos"
                element={
                  <AdminRoute>
                    <AdminVenuePagos />
                  </AdminRoute>
                }
              />
              <Route
                path="users"
                element={
                  <AdminRoute>
                    <Users />
                  </AdminRoute>
                }
              />
              <Route
                path="venue-requests"
                element={
                  <AdminRoute>
                    <VenueRequests />
                  </AdminRoute>
                }
              />
              <Route
                path="open-data"
                element={
                  <AdminRoute>
                    <OpenDataManager />
                  </AdminRoute>
                }
              />
              <Route
                path="catalogos"
                element={
                  <AdminRoute>
                    <CatalogosManager />
                  </AdminRoute>
                }
              />
              <Route
                path="tipos-evento"
                element={
                  <AdminRoute>
                    <EventTypesManager />
                  </AdminRoute>
                }
              />
              <Route
                path="ubicaciones"
                element={
                  <AdminRoute>
                    <CiudadesManager />
                  </AdminRoute>
                }
              />
              <Route
                path="temas-destino"
                element={
                  <AdminRoute>
                    <TemasDestinoManager />
                  </AdminRoute>
                }
              />
              <Route
                path="proveedor-categorias"
                element={
                  <AdminRoute>
                    <ProveedoresCategoriasManager />
                  </AdminRoute>
                }
              />
              <Route
                path="feedback"
                element={
                  <AdminRoute>
                    <FeedbackAdmin />
                  </AdminRoute>
                }
              />
              {/* Admin Stats es ahora el reporte "Actividad por venue" dentro de
                  Reportes admin. La ruta vieja se conserva para los links
                  guardados. */}
              <Route
                path="admin-stats"
                element={<Navigate to="/dashboard/reportes-admin?r=actividad-venues" replace />}
              />
              <Route
                path="admin-telegram"
                element={
                  <AdminRoute>
                    <AdminTelegramBoard />
                  </AdminRoute>
                }
              />
              <Route
                path="blog"
                element={
                  <AdminRoute>
                    <BlogList />
                  </AdminRoute>
                }
              />
              <Route
                path="blog/new"
                element={
                  <AdminRoute>
                    <BlogForm />
                  </AdminRoute>
                }
              />
              <Route
                path="blog/:id"
                element={
                  <AdminRoute>
                    <BlogForm />
                  </AdminRoute>
                }
              />
              <Route
                path="marketing/templates"
                element={
                  <AdminRoute>
                    <Suspense fallback={<MarketingFallback />}>
                      <MarketingTemplateList />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="marketing/templates/:id"
                element={
                  <AdminRoute>
                    <Suspense fallback={<MarketingFallback />}>
                      <MarketingTemplateEditor />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="marketing/campaigns"
                element={
                  <AdminRoute>
                    <Suspense fallback={<MarketingFallback />}>
                      <MarketingCampaignList />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="marketing/campaigns/:id"
                element={
                  <AdminRoute>
                    <Suspense fallback={<MarketingFallback />}>
                      <MarketingCampaignEditor />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="marketing"
                element={
                  <AdminRoute>
                    <Navigate to="/dashboard/marketing/campaigns" replace />
                  </AdminRoute>
                }
              />

              {/* Regular Private Routes */}
              <Route path="venues" element={<NoViewerRoute><NoInversionistaRoute><VenuesList /></NoInversionistaRoute></NoViewerRoute>} />
              <Route path="venues/:id" element={<NoViewerRoute><NoInversionistaRoute><VenuePage /></NoInversionistaRoute></NoViewerRoute>} />
              <Route path="new-venue" element={<NoViewerRoute><NoInversionistaRoute><CreateVenue /></NoInversionistaRoute></NoViewerRoute>} />
              <Route path="calendario" element={<Calendario />} />
              <Route path="leads" element={<Formularios />} />
              <Route path="leads/:id" element={<LeadPage />} />
              {/* Directorio (FRA-353): proveedores + planners + clientes. Las
                  URLs viejas de proveedores siguen vivas. */}
              <Route path="directorio" element={<NoViewerRoute><DirectorioPage /></NoViewerRoute>} />
              <Route path="proveedores" element={<Navigate to="/dashboard/directorio" replace />} />
              <Route path="proveedores/:id" element={<NoViewerRoute><ProveedorPage /></NoViewerRoute>} />
              <Route path="clientes/:id" element={<NoViewerRoute><ClientePage /></NoViewerRoute>} />
              <Route path="servicios" element={<NoViewerRoute><ServiciosPage /></NoViewerRoute>} />
              <Route path="servicios/:servicioId" element={<NoViewerRoute><ServicioDetallePage /></NoViewerRoute>} />
              {/* Recursos se llama Servicios (FRA-347): las URLs viejas redirigen. */}
              <Route path="recursos" element={<Navigate to="/dashboard/servicios" replace />} />
              <Route path="recursos/:servicioId" element={<RedirectServicio />} />
              <Route path="eventos" element={<EventDetails />} />
              <Route path="eventos/:eventId" element={<EventPage />} />
              <Route path="cuentas" element={<NoViewerRoute><CuentasShell /></NoViewerRoute>}>
                <Route index element={<Navigate to="por-cobrar" replace />} />
                <Route path="por-cobrar" element={<CuentasPorCobrar />} />
                <Route path="por-pagar" element={<CuentasPorPagar />} />
                <Route path="balance" element={<CuentasBalance />} />
                <Route path="depositos" element={<CuentasDepositos />} />
                <Route path="movimientos" element={<CuentasMovimientos />} />
                {/* URLs viejas: las pestañas fusionadas (FRA-344) caen en
                    Balance general, y "Deudas" volvió a llamarse Por pagar,
                    para que haga par con Por cobrar. */}
                <Route path="pagos" element={<Navigate to="/dashboard/cuentas/balance" replace />} />
                <Route path="reembolsos" element={<Navigate to="/dashboard/cuentas/balance" replace />} />
                <Route path="costos" element={<Navigate to="/dashboard/cuentas/balance" replace />} />
                <Route path="deudas" element={<Navigate to="/dashboard/cuentas/por-pagar" replace />} />
              </Route>
              <Route path="pagos" element={<Navigate to="/dashboard/cuentas/balance" replace />} />
              <Route path="reportes" element={<NoViewerRoute><ReportesPage /></NoViewerRoute>} />
              {/* Reportes de plataforma (cruzan todos los venues). Aparte de
                  Reportes, que siempre habla del venue seleccionado. */}
              <Route path="reportes-admin" element={<AdminRoute><ReportesAdminPage /></AdminRoute>} />
              <Route path="reportes/:reporteId" element={<NoViewerRoute><ReporteDetalle /></NoViewerRoute>} />
              <Route path="bitacora" element={<NoViewerRoute><NoInversionistaRoute><Bitacora /></NoInversionistaRoute></NoViewerRoute>} />
              <Route path="config" element={<NoViewerRoute><NoInversionistaRoute><Configuracion /></NoInversionistaRoute></NoViewerRoute>} />
              <Route path="ayuda" element={<Support />} />
              <Route path="changelog" element={<Changelog />} />
              <Route path="perfil" element={<UserProfile />} />

              {/* Default Routes */}
              <Route index element={<Navigate to="home" replace />} />
              <Route path="*" element={<Navigate to="home" replace />} />
            </Route>

            {/* Fallback Route - Changed to home for all users */}
            <Route
              path="*"
              element={<Navigate to="/dashboard/home" replace />}
            />
          </Routes>
        </Router>
      </AuthProvider>
      <UpdateDialog />
    </AlertProvider>
  );
}

export default App;
